import { useEffect } from 'react';
import { FaShoppingCart, FaHeart } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    add_to_card,
    get_wishlist_products,
    remove_wishlist,
    messageClear,
} from '../../store/reducers/cardReducer';
import toast from 'react-hot-toast';
import Rating from '../Rating';
import { useCurrency } from '../../context/CurrencyContext';

const Wishlist = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { userInfo }                = useSelector(s => s.auth);
    const { wishlist, successMessage } = useSelector(s => s.card);
    const { formatBase }              = useCurrency();

    useEffect(() => {
        dispatch(get_wishlist_products(userInfo.id));
    }, []);

    useEffect(() => {
        if (successMessage) {
            toast.success(successMessage);
            dispatch(messageClear());
        }
    }, [successMessage]);

    const addToCart = (id) => {
        if (userInfo) {
            dispatch(add_to_card({ userId: userInfo.id, quantity: 1, productId: id }));
        } else {
            navigate('/login');
        }
    };

    return (
        <div className="space-y-3">
            {/* Header card */}
            <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-800">
                    My Wishlist
                </span>
                <span className="text-xs text-gray-500">
                    {wishlist.length} item{wishlist.length !== 1 ? 's' : ''}
                </span>
            </div>

            {wishlist.length > 0 ? (
                <div className="grid grid-cols-4 xl:grid-cols-3 md-lg:grid-cols-2 sm:grid-cols-2 xs:grid-cols-1 gap-3">
                    {wishlist.map((p, i) => {
                        const finalPrice = p.price - Math.floor((p.price * p.discount) / 100);

                        return (
                            <div
                                key={i}
                                className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200 group"
                            >
                                {/* Image */}
                                <div className="relative overflow-hidden bg-gray-50">
                                    {p.discount !== 0 && (
                                        <div className="absolute left-2 top-2 z-10 bg-orange-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
                                            -{p.discount}%
                                        </div>
                                    )}
                                    <button
                                        onClick={() => dispatch(remove_wishlist(p._id))}
                                        className="absolute right-2 top-2 z-10 w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center shadow-sm hover:bg-red-50 hover:border-red-200 transition-all"
                                    >
                                        <FaHeart className="text-red-400 text-xs" />
                                    </button>
                                    <Link to={`/product/details/${p.slug}`}>
                                        <img
                                            className="w-full h-[180px] object-cover group-hover:scale-105 transition-transform duration-500"
                                            src={p.image}
                                            alt={p.name}
                                        />
                                    </Link>
                                </div>

                                {/* Content */}
                                <div className="p-3">
                                    <Link to={`/product/details/${p.slug}`}>
                                        <h2 className="text-[12px] text-gray-800 leading-snug line-clamp-2 hover:text-orange-500 transition-colors">
                                            {p.name}
                                        </h2>
                                    </Link>

                                    <div className="mt-1 flex items-center gap-1">
                                        <Rating ratings={p.rating} />
                                        <span className="text-[10px] text-gray-400">({p.rating})</span>
                                    </div>

                                    <div className="mt-1.5 flex items-baseline gap-1.5">
                                        <span className="text-sm font-bold text-gray-900">
                                            {formatBase(finalPrice)}
                                        </span>
                                        {p.discount !== 0 && (
                                            <span className="text-[11px] text-gray-400 line-through">
                                                {formatBase(p.price)}
                                            </span>
                                        )}
                                    </div>

                                    <button
                                        onClick={() => addToCart(p._id)}
                                        className="mt-2.5 w-full h-8 rounded-full bg-orange-500 hover:bg-orange-600 text-[11px] text-white font-semibold flex items-center justify-center gap-1.5 transition-colors"
                                    >
                                        <FaShoppingCart className="text-[10px]" />
                                        Add to Cart
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="bg-white rounded-lg border border-gray-200 flex flex-col items-center py-16">
                    <div className="w-14 h-14 rounded-full bg-orange-50 flex items-center justify-center mb-3">
                        <FaHeart className="text-orange-400 text-xl" />
                    </div>
                    <p className="text-sm font-semibold text-gray-700">Your wishlist is empty</p>
                    <p className="text-xs text-gray-400 mt-1 mb-5">Save items you love and shop them later</p>
                    <Link
                        to="/products"
                        className="px-6 py-2 rounded-full bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold transition-colors"
                    >
                        Continue Shopping
                    </Link>
                </div>
            )}
        </div>
    );
};

export default Wishlist;
